import { useLocation } from "react-router-dom"
import { Star } from "@phosphor-icons/react"
import { usePinnedPagesStore } from "../../stores"
import { cn } from "../../lib/utils"

interface RouteInfo {
  label: string
  section?: string
}

// Map paths to labels and sections
const routeConfig: Record<string, RouteInfo> = {
  // Top-level pages
  "/": { label: "Today's Activity" },
  "/communication": { label: "Call Center" },
  "/human-actions": { label: "Human Actions" },
  "/user-activity": { label: "User Activity" },

  // Analytics
  "/historical/daily": { label: "Historical", section: "Analytics" },
  "/month-to-date": { label: "Month To Date", section: "Analytics" },
  "/pipeline-breakdown": { label: "Pipeline Breakdown", section: "Analytics" },
  "/claimables": { label: "Claimables", section: "Analytics" },

  // Billing
  "/payments": { label: "Payments", section: "Billing" },
  "/delinquent": { label: "Delinquent Matters", section: "Billing" },
  "/subscriptions": { label: "Recurring Plans", section: "Billing" },
  "/subscriptions/over-time": { label: "Plans Over Time", section: "Billing" },
  "/expired-methods": { label: "Expired Methods", section: "Billing" },
  "/revenue-projections": { label: "Revenue Projections", section: "Billing" },

  // Quality Control
  "/user-effectiveness": { label: "User Effectiveness", section: "Quality Control" },
  "/phone-calls-users": { label: "User Phone Calls", section: "Quality Control" },
  "/expected-dates": { label: "Expected Dates", section: "Quality Control" },
  "/event-calls": { label: "Event Calls", section: "Quality Control" },
  "/items-calls": { label: "Items Calls", section: "Quality Control" },
  "/matters-overdue": { label: "Matters Overdue", section: "Quality Control" },

  // Action Required
  "/approvals": { label: "Approvals", section: "Action Required" },
  "/requests": { label: "Requests", section: "Action Required" },
  "/tasks": { label: "Tasks", section: "Action Required" },
  "/outstanding-items": { label: "Outstanding Items", section: "Action Required" },
  "/general-items": { label: "General Items", section: "Action Required" },

  // Communications
  "/messaging": { label: "Messaging", section: "Communications" },
  "/follow-ups": { label: "Follow Ups", section: "Communications" },
  "/calls": { label: "Phone Calls", section: "Communications" },
  "/voicemails": { label: "Voicemails", section: "Communications" },
  "/text-threads": { label: "Text Messages", section: "Communications" },
  "/undelivered-texts": { label: "Undelivered Texts", section: "Communications" },

  // Documents
  "/uploads": { label: "Uploads", section: "Documents" },
  "/document-uploads": { label: "Document Uploads", section: "Documents" },
  "/signatures": { label: "Signatures", section: "Documents" },
  "/field-audits": { label: "Field Audits", section: "Documents" },

  // Pipeline
  "/leads": { label: "Leads", section: "Pipeline" },
  "/bookings": { label: "Bookings", section: "Pipeline" },
  "/user-pipelines": { label: "User Pipelines", section: "Pipeline" },

  // Deep Dive
  "/user-notes": { label: "User Notes", section: "Deep Dive" },
  "/events": { label: "Events", section: "Deep Dive" },
  "/matter-alerts": { label: "Matter Alerts", section: "Deep Dive" },

  // Bankruptcy
  "/bk/daily-reports": { label: "Daily Reports", section: "Bankruptcy" },
  "/bk/items": { label: "Tasks / Items", section: "Bankruptcy" },
  "/bk/docket-search": { label: "Docket Search", section: "Bankruptcy" },
  "/bk/cases-filed": { label: "Cases Filed", section: "Bankruptcy" },
  "/bk/hearings": { label: "Hearings", section: "Bankruptcy" },
  "/bk/filing-fee-deadlines": { label: "Filing Fee Deadlines", section: "Bankruptcy" },
  "/bk/trustees": { label: "Trustees", section: "Bankruptcy" },
  "/bk/trustee-uploads": { label: "Trustee Uploads", section: "Bankruptcy" },

  // Settings
  "/settings": { label: "Settings" },
  "/settings/profile": { label: "Profile", section: "Settings" },
}

function getRouteInfo(pathname: string): RouteInfo {
  // Direct match
  if (routeConfig[pathname]) {
    return routeConfig[pathname]
  }

  // Try to match parent path for nested routes
  const segments = pathname.split("/").filter(Boolean)
  while (segments.length > 0) {
    const parentPath = "/" + segments.join("/")
    if (routeConfig[parentPath]) {
      return routeConfig[parentPath]
    }
    segments.pop()
  }

  // Fallback - format the path as a label
  const lastSegment = pathname.split("/").filter(Boolean).pop() || "Dashboard"
  return {
    label: lastSegment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }
}

export function PageBreadcrumb() {
  const location = useLocation()
  const { isPinned, togglePin } = usePinnedPagesStore()

  const routeInfo = getRouteInfo(location.pathname)
  const fullPath = location.pathname + location.search
  const pinned = isPinned(fullPath)

  const handleTogglePin = () => {
    togglePin({
      path: fullPath,
      label: routeInfo.label,
      section: routeInfo.section,
    })
  }

  return (
    <div className="hidden lg:flex items-center gap-2">
      <div className="flex items-center text-sm">
        {routeInfo.section && (
          <>
            <span className="text-muted-foreground">{routeInfo.section}</span>
            <span className="mx-2 text-muted-foreground/50">/</span>
          </>
        )}
        <span className="font-medium text-foreground">{routeInfo.label}</span>
      </div>

      <button
        onClick={handleTogglePin}
        className={cn(
          "flex items-center justify-center h-6 w-6 rounded transition-colors",
          pinned
            ? "text-amber-500 hover:text-amber-600"
            : "text-muted-foreground/50 hover:text-muted-foreground"
        )}
        title={pinned ? "Unpin this page" : "Pin this page"}
      >
        <Star className="h-4 w-4" weight={pinned ? "fill" : "regular"} />
      </button>
    </div>
  )
}
